import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { intakeToSections, sectionLabel, fieldLabel, humanValue } from "@/lib/intakeView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, ArrowLeft, FileText, Package, DollarSign, Ruler, ImageIcon, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";

const QTY_BADGE = {
  known: "bg-emerald-500/20 text-emerald-300", calculated: "bg-violet-500/20 text-violet-300",
  allowance: "bg-sky-500/20 text-sky-300", selection_pending: "bg-amber-500/20 text-amber-300", office_verify: "bg-rose-500/20 text-rose-300",
};

export default function OfficeReview() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState("");
  const [amount, setAmount] = useState("");
  const [priceNotes, setPriceNotes] = useState("");
  const pollingRef = useRef(false);

  const load = async () => {
    const { data } = await api.get(`/projects/${projectId}`);
    setP(data);
    if (data.estimate) { setAmount(String(data.estimate.amount)); setPriceNotes(data.estimate.notes || ""); }
  };
  useEffect(() => { load(); }, [projectId]);

  useEffect(() => {
    if (!p || pollingRef.current) return;
    if (p.scope_generating || p.materials_generating) {
      pollingRef.current = true;
      (async () => {
        if (p.scope_generating) await pollUntil((d) => !d.scope_generating && d.scope);
        if (p.materials_generating) await pollUntil((d) => !d.materials_generating && d.materials);
        pollingRef.current = false;
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.scope_generating, p?.materials_generating]);

  const pollUntil = async (check, timeout = 240000) => {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      await new Promise((r) => setTimeout(r, 4000));
      const { data } = await api.get(`/projects/${projectId}`);
      setP(data);
      if (check(data)) return data;
    }
    return null;
  };

  const genScope = async () => {
    setBusy("scope");
    try {
      await api.post(`/projects/${projectId}/scope`);
      toast.info("Generating Scope of Work…");
      const done = await pollUntil((d) => !d.scope_generating && d.scope);
      if (done) toast.success("Scope of Work generated"); else toast.error("Scope generation timed out");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const genMaterials = async () => {
    setBusy("materials");
    try {
      await api.post(`/projects/${projectId}/materials`);
      toast.info("Generating Material Requirements…");
      const done = await pollUntil((d) => !d.materials_generating && d.materials);
      if (done) toast.success("Material Requirements generated"); else toast.error("Materials generation timed out");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const savePrice = async () => {
    if (!amount) { toast.error("Enter an amount"); return; }
    setBusy("price");
    try { await api.post(`/projects/${projectId}/estimate`, { amount: Number(amount), notes: priceNotes, line_items: [] }); toast.success("Price sent — salesperson notified!"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!p) return <AppShell><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div></AppShell>;

  const sections = intakeToSections(p.intake || {});

  return (
    <AppShell title="Job Intake Package">
      <div className="p-4 sm:p-6 pb-20">
        <button data-testid="office-back-button" onClick={() => navigate("/office")} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4"><ArrowLeft className="w-4 h-4" /> Estimating Queue</button>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-3xl font-extrabold">{p.customer_name}</h1>
            <div className="text-slate-500">{p.address} · Salesperson: {p.salesperson_name}</div>
          </div>
          <StatusBadge status={p.status} />
        </div>

        <Tabs defaultValue="package">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="package" data-testid="tab-package">Intake Package</TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos">Photos ({(p.photos || []).length})</TabsTrigger>
            <TabsTrigger value="scope" data-testid="tab-scope">Scope of Work</TabsTrigger>
            <TabsTrigger value="materials" data-testid="tab-materials">Materials</TabsTrigger>
            <TabsTrigger value="estimate" data-testid="tab-estimate">Estimate</TabsTrigger>
          </TabsList>

          {/* Package */}
          <TabsContent value="package" className="mt-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-3">Project Information</div>
                <Row k="Customer" v={p.customer_name} /><Row k="Address" v={p.address} /><Row k="Phone" v={p.phone} /><Row k="Type" v="Full Bathroom Remodel" />
              </div>
              {sections.map((sec) => (
                <div key={sec.key} className="rounded-2xl bg-slate-900 border border-slate-800 p-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-3">{sectionLabel(sec.key)}</div>
                  {sec.fields.map(([k, v]) => <Row key={k} k={fieldLabel(k)} v={humanValue(v)} />)}
                </div>
              ))}
              <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-3 flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5" /> Measurements</div>
                {(p.measurements || []).length === 0 ? <div className="text-sm text-slate-600">None recorded</div> :
                  p.measurements.map((m) => <Row key={m.id} k={m.label} v={`${m.value} ${m.unit}${m.source === "calculated" ? " (calc)" : ""}`} />)}
              </div>
              <div className="rounded-2xl bg-slate-900 border border-slate-800 p-5">
                <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-3">Customer Selections</div>
                {(p.selections || []).length === 0 ? <div className="text-sm text-slate-600">None recorded</div> :
                  p.selections.map((s) => <Row key={s.id} k={s.category} v={`${s.status}${s.detail ? " — " + s.detail : ""}`} />)}
              </div>
              {p.verification?.office_attention?.length > 0 && (
                <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-5 lg:col-span-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Office Review Flags</div>
                  <ul className="text-sm space-y-1 text-amber-100">{p.verification.office_attention.map((o, i) => <li key={i}>• {o}</li>)}</ul>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Photos */}
          <TabsContent value="photos" className="mt-5">
            {(p.photos || []).length === 0 ? <div className="text-slate-500">No photos submitted.</div> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {p.photos.map((ph) => (
                  <div key={ph.id} className="rounded-xl overflow-hidden border border-slate-800 bg-slate-900">
                    <img src={fileUrl(ph.storage_path)} alt={ph.category} className="w-full h-44 object-cover" />
                    <div className="p-2"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 capitalize">{ph.category}</span>{ph.note && <div className="text-xs text-slate-500 mt-1">{ph.note}</div>}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Scope */}
          <TabsContent value="scope" className="mt-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">AI-generated from verified field information. Review before finalizing.</p>
              <Button data-testid="generate-scope-button" onClick={genScope} disabled={busy === "scope"} className="bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold">
                {busy === "scope" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> {p.scope ? "Regenerate" : "Generate"} Scope</>}
              </Button>
            </div>
            {!p.scope ? (p.scope_generating ? <Generating text="Generating Scope of Work — this can take a few minutes…" /> : <Empty icon={FileText} text="No scope yet. Click Generate Scope." />) : (
              <div className="space-y-4">
                {p.scope.sections?.map((s, i) => (
                  <div key={i} className="rounded-2xl bg-slate-900 border border-slate-800 p-5" data-testid={`scope-section-${i}`}>
                    <div className="font-display text-lg font-bold text-orange-300 mb-2">{s.title}</div>
                    <ul className="space-y-1 text-sm text-slate-200">{s.items?.map((it, j) => <li key={j} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-slate-600 mt-0.5 shrink-0" /> {it}</li>)}</ul>
                  </div>
                ))}
                {p.scope.allowances?.length > 0 && <Box title="Allowances" items={p.scope.allowances} color="sky" />}
                {p.scope.exclusions?.length > 0 && <Box title="Exclusions" items={p.scope.exclusions} color="slate" />}
                {p.scope.verify_items?.length > 0 && <Box title="Verify / Unknown Items" items={p.scope.verify_items} color="amber" />}
              </div>
            )}
          </TabsContent>

          {/* Materials */}
          <TabsContent value="materials" className="mt-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">Organized by trade. Quantity types are labeled.</p>
              <Button data-testid="generate-materials-button" onClick={genMaterials} disabled={busy === "materials"} className="bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold">
                {busy === "materials" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> {p.materials ? "Regenerate" : "Generate"} Materials</>}
              </Button>
            </div>
            {!p.materials ? (p.materials_generating ? <Generating text="Generating Material Requirements — this can take a few minutes…" /> : <Empty icon={Package} text="No material list yet. Click Generate Materials." />) : (
              <div className="space-y-4">
                {p.materials.categories?.map((c, i) => (
                  <div key={i} className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden" data-testid={`material-category-${i}`}>
                    <div className="px-5 py-3 bg-slate-800/50 font-display font-bold text-orange-300">{c.category}</div>
                    <div className="divide-y divide-slate-800">
                      {c.items?.map((it, j) => (
                        <div key={j} className="px-5 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`material-item-${i}-${j}`}>
                          <div><div className="font-medium">{it.name}</div>{it.notes && <div className="text-xs text-slate-500">{it.notes}</div>}</div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="font-mono-data text-slate-300">{it.quantity}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${QTY_BADGE[it.qty_type] || "bg-slate-700 text-slate-300"}`}>{(it.qty_type || "").replace(/_/g, " ")}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Estimate */}
          <TabsContent value="estimate" className="mt-5">
            <div className="max-w-lg rounded-2xl bg-slate-900 border border-slate-800 p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-orange-400 mb-4 flex items-center gap-1.5"><DollarSign className="w-4 h-4" /> Approve Final Price</div>
              <Label className="text-slate-400 text-xs">Estimate amount ($)</Label>
              <Input data-testid="estimate-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 24500" className="bg-slate-950 border-slate-700 mt-1 h-12 text-lg font-mono-data" />
              <Label className="text-slate-400 text-xs mt-4 block">Notes to salesperson (optional)</Label>
              <Textarea value={priceNotes} onChange={(e) => setPriceNotes(e.target.value)} rows={3} className="bg-slate-950 border-slate-700 mt-1" placeholder="Assumptions, allowances, valid-through date…" />
              <Button data-testid="save-estimate-button" onClick={savePrice} disabled={busy === "price"} className="w-full mt-5 h-12 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold text-base">
                {busy === "price" ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Price to Salesperson"}
              </Button>
              {p.estimate && <div className="mt-4 text-sm text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Current price: ${p.estimate.amount.toLocaleString()} (sent {new Date(p.estimate.at).toLocaleString()})</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

const Row = ({ k, v }) => <div className="flex items-center justify-between py-1 text-sm border-b border-slate-800/50 last:border-0"><span className="text-slate-400">{k}</span><span className="font-medium text-slate-100 font-mono-data text-right">{v}</span></div>;
const Empty = ({ icon: Icon, text }) => <div className="rounded-2xl bg-slate-900/50 border border-dashed border-slate-800 p-12 text-center text-slate-500"><Icon className="w-8 h-8 mx-auto mb-3 text-slate-700" />{text}</div>;
const Generating = ({ text }) => <div data-testid="generating-state" className="rounded-2xl bg-slate-900/50 border border-dashed border-orange-500/30 p-12 text-center text-orange-300"><Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" />{text}</div>;
const Box = ({ title, items, color }) => {
  const c = { sky: "bg-sky-500/10 border-sky-500/30 text-sky-300", slate: "bg-slate-800/50 border-slate-700 text-slate-300", amber: "bg-amber-500/10 border-amber-500/30 text-amber-300" }[color];
  return <div className={`rounded-2xl border p-5 ${c}`}><div className="text-xs font-bold uppercase tracking-wider mb-2">{title}</div><ul className="text-sm space-y-1">{items.map((it, i) => <li key={i}>• {it}</li>)}</ul></div>;
};
